import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WearableMetric {
  id: string;
  user_id: string;
  provider: string;
  record_date: string;
  sleep_duration?: number;
  sleep_efficiency?: number;
  hrv?: number;
  rhr?: number;
  rem_minutes?: number;
  deep_minutes?: number;
  readiness_score?: number;
  sleep_score?: number;
  steps?: number;
  calories_burned?: number;
  active_minutes?: number;
  brpm?: number;
  sleep_onset?: number; // epoch ms
  wakeup_time?: number; // epoch ms
  created_at: string;
  updated_at: string;
}

export const useLatestWearableMetrics = () => {
  return useQuery({
    queryKey: ['wearable-metrics'],
    queryFn: async (): Promise<WearableMetric[]> => {
      console.log('📊 Fetching wearable metrics...');
      
      const { data, error } = await supabase
        .from('wearable_metrics')
        .select('*')
        .order('record_date', { ascending: false })
        .limit(30); // Get last 30 days

      if (error) {
        console.error('❌ Error fetching wearable metrics:', error);
        throw error;
      }

      console.log('📈 Wearable metrics fetched:', {
        count: data?.length || 0,
        providers: [...new Set(data?.map(m => m.provider))],
        dateRange: data?.length > 0 ? {
          latest: data[0]?.record_date,
          earliest: data[data.length - 1]?.record_date
        } : null,
        sampleData: data?.slice(0, 3), // Show first 3 records
        allData: data // Log all data for debugging
      });

      return data || [];
    },
    refetchInterval: 300000, // Reduced from 5 seconds to 5 minutes
    staleTime: 240000, // Consider data stale after 4 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

export const useWearableMetricsByProvider = (provider: string) => {
  return useQuery({
    queryKey: ['wearable-metrics', provider],
    queryFn: async (): Promise<WearableMetric[]> => {
      console.log(`📊 Fetching ${provider} metrics...`);
      
      const { data, error } = await supabase
        .from('wearable_metrics')
        .select('*')
        .eq('provider', provider)
        .order('record_date', { ascending: false })
        .limit(30);

      if (error) {
        console.error(`❌ Error fetching ${provider} metrics:`, error);
        throw error;
      }

      console.log(`📈 ${provider} metrics fetched:`, {
        count: data?.length || 0,
        dateRange: data?.length > 0 ? {
          latest: data[0]?.record_date,
          earliest: data[data.length - 1]?.record_date
        } : null,
        allRecords: data
      });

      return data || [];
    },
    staleTime: 240000, // 4 minutes
    refetchInterval: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
